/**
 * ScrollSmoother Manager
 * Responsive, accessible, dialog-aware
 */
(() => {
  if (Shopify.designMode) return;

  // Prevent scroll restoration
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
  scrollTo(0, 0);

  const CONFIG = {
    wrapper: '#smooth-wrapper',
    content: '#smooth-content',
    breakpoint: 750,
    smooth: 1
  };

  let smoother = null;
  let resizeTimer;

  const shouldBeActive = () =>
    innerWidth >= CONFIG.breakpoint &&
    !matchMedia('(prefers-reduced-motion: reduce)').matches;

  function create() {
    if (smoother || !shouldBeActive()) return;
    if (!document.querySelector(CONFIG.wrapper)) return;

    smoother = ScrollSmoother.create({
      wrapper: CONFIG.wrapper,
      content: CONFIG.content,
      smooth: CONFIG.smooth,
      smoothTouch: false,
      effects: '[data-speed], [data-lag]'
    });

    document.documentElement.classList.add('smooth-active');
  }

  function destroy() {
    if (!smoother) return;

    const y = smoother.scrollTop();
    smoother.kill();
    smoother = null;

    document.documentElement.classList.remove('smooth-active');
    scrollTo(0, y);
  }

  function handleResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      shouldBeActive() ? create() : destroy();
    }, 150);
  }

  function watchDialogs() {
    const toggle = (dialog) => {
      if (!smoother) return;

      if (dialog.hasAttribute('open')) {
        smoother.smooth(0);
        smoother.paused(true);
      } else {
        smoother.paused(false);
        requestAnimationFrame(() => {
          if (smoother) smoother.smooth(CONFIG.smooth);
        });
      }
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'open') toggle(m.target);
      }
    });

    const observe = (el) => observer.observe(el, {
      attributes: true,
      attributeFilter: ['open']
    });

    document.querySelectorAll('dialog[scroll-lock]').forEach(observe);

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.('dialog[scroll-lock]')) observe(n);
          n.querySelectorAll?.('dialog[scroll-lock]').forEach(observe);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

    addEventListener('resize', handleResize);
    matchMedia('(prefers-reduced-motion: reduce)')
      .addEventListener('change', handleResize);

    document.addEventListener('shopify:section:load', () => smoother?.refresh());
    document.addEventListener('shopify:section:unload', () => smoother?.refresh());

    create();
    watchDialogs();
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

  // Public API
  window.scrollSmootherManager = {
    get: () => smoother,
    scrollTo: (target, smooth = true) => smoother?.scrollTo(target, smooth),
    refresh: () => ScrollTrigger.refresh(),
    disable: destroy,
    enable: create
  };
})();

/**
 * ScrollTo Manager
 * Works with ScrollSmoother when active, falls back to GSAP ScrollToPlugin
*/
(() => {
  const DEFAULTS = {
    duration: 0.3,
    ease: 'power1.inOut',
    offsetY: 68
  };

  function scrollToTarget(target, opts = {}) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;

    const { duration, ease, offsetY } = { ...DEFAULTS, ...opts };
    const smoother = window.scrollSmootherManager?.get();

    if (smoother) {
      smoother.scrollTo(el, true, `top top+=${offsetY}`);
    } else {
      gsap.to(window, {
        scrollTo: { y: el, offsetY },
        duration,
        ease
      });
    }
  }

  function handleClick(e) {
    const anchor = e.target.closest('a[href*="#"]');
    if (!anchor) return;

    const url = new URL(anchor.href, location.href);

    // Only handle same-page anchors
    if (url.pathname !== location.pathname || !url.hash) return;

    const target = document.querySelector(url.hash);
    if (!target) return;

    e.preventDefault();
    history.pushState(null, '', url.hash);
    scrollToTarget(target, anchor.dataset);
  }

  function handleHashChange() {
    if (!location.hash) return;
    const target = document.querySelector(location.hash);
    if (target) scrollToTarget(target);
  }

  function handleInitialHash() {
    if (!location.hash) return;
    // Delay to let ScrollSmoother initialize
    requestAnimationFrame(() => {
      const target = document.querySelector(location.hash);
      if (target) scrollToTarget(target);
    });
  }

  function init() {
    gsap.registerPlugin(ScrollToPlugin);

    document.addEventListener('click', handleClick);
    addEventListener('hashchange', handleHashChange);
    handleInitialHash();
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

  // Public API
  window.scrollToManager = {
    to: scrollToTarget
  };
})();
